import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished } from '../utils/constants';
import GameHeader from './GameHeader';
import H2hTable from './H2hTable';
import TotalsTable from './TotalsTable';
import SpreadsTable from './SpreadsTable';
import H2hAllTable from './H2hAllTable';
import TotalsAllTable from './TotalsAllTable';
import SpreadsAllTable from './SpreadsAllTable';
import H2hMiniTable from './H2hMiniTable';
import TotalsMiniTable from './TotalsMiniTable';
import SpreadsMiniTable from './SpreadsMiniTable';

const NCAAFDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onOddsClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'ncaaf', ...game };

  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      // Market just changed, set rendering state synchronously
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;
  
  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);
  
  // Get visible books based on platform
  const bookLogos = isMobile 
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;
    
  // Navigation functions for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    delta: 50 // Minimum distance for swipe
  });

  const oddsCellStyle = { 
    width: '80px',
    minWidth: '80px',
    maxWidth: '80px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // NCAAF-specific live status formatter
  const formatLiveStatus = (status, game, safeGame) => {
    if (status === 'Live' || status === 'In Progress' || status === 'InProgress') {
      return (
        <span style={{ 
          backgroundColor: 'red', 
          color: 'white', 
          padding: '2px 6px', 
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          display: 'inline-block'
        }}>
          LIVE
        </span>
      );
    }
    return null;
  };

  // Format game time display
  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return 'TBD';
    if (gameStatus === 'Live') return 'LIVE';
    if (isGameFinished(gameStatus)) return gameStatus.toUpperCase();
    
    // Check if it's already in 12-hour format
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    // Convert 24-hour to 12-hour format
    const [hour, minute] = timeStr.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const timeDisplay = formatTime(gameTime, gameStatus);
  const isLive = gameStatus === 'Live';
  const isFinished = isGameFinished(gameStatus);

  const renderPrice = (odds, direction = null) => {
    if (odds === null || odds === undefined) return '\u00A0';
    
    const formattedOdds = odds > 0 ? `+${odds}` : `${odds}`;
    const color = odds > 0 ? '#28a745' : '#dc3545';
    
    return (
      <span style={{ color, fontWeight: 'bold' }}>
        {formattedOdds}
      </span>
    );
  };

  // Utility functions for the reusable components
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count, direction) => {
    const style = {};
    const occurrences = [price].filter(p => p === bestBookPrice).length;
    
    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1 && occurrences === 1) {
      style.fontWeight = 'bold';
      
      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text (original design)
          Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text for better contrast
          Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
          Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
        } else {
          // Best line but below tier thresholds - use tier 2 amber color
          Object.assign(style, { color: '#F59E0B' });
        }
      }
    }
    
    return style;
  };

  const getTooltipText = (price, bestBookPrice, bestBookDiff, count) => {
    const occurrences = [price].filter(p => p === bestBookPrice).length;
    if (price === bestBookPrice && bestBookDiff !== 1 && count > 1 && occurrences === 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue > 0) {
        return `${(diffValue * 100).toFixed(1)}% edge`;
      }
    }
    return null;
  };

  // Only debug the first game to reduce console storm
  const isFirstGame = safeGame.gameIDmlb && safeGame.gameIDmlb.toString().endsWith('1');
  
  // Filter data based on selected market (using original DataTable logic)
  const filteredData = displayData
    ? displayData.filter(player => {
        if (!showTiers && player.tier && player.tier !== 'Main') {
          return false;
        }
        if (masterTierFilter && player.tier !== masterTierFilter) {
          return false;
        }
        if (selectedMarket === 'last_td_scorer' && !player.playerId?.includes('last_td_scorer')) {
          return false;
        }
        if (selectedMarket === 'first_td_scorer' && !player.playerId?.includes('first_td_scorer')) {
          return false;
        }
        return true;
      })
    : [];

  if (isFirstGame) {
    if (displayData && displayData.length > 0) {
      if (displayData[0].markets) {
      }
      if (displayData[0].playerId) {
      }
    }
  }

  // For h2h market, separate data into different categories
  let h2hData = [];
  let totalsData = [];
  let spreadsData = [];

  if (selectedMarket === 'h2h' || selectedMarket === 'h2h_all' || selectedMarket === 'h2h_quarters' || selectedMarket === 'h2h_halves') {
    if (isFirstGame) {
    }
    // Separate all data by type (h2h, totals, spreads)
    filteredData.forEach(player => {
      const playerId = player.playerId || '';

      // Categorize by bet type
      if (playerId.startsWith('totals_')) {
        totalsData.push(player);
      } else if (playerId.startsWith('spreads_')) {
        spreadsData.push(player);
      } else {
        // All h2h data (game, quarters, halves)
        h2hData.push(player);
      }
    });
    if (isFirstGame) {
    }
  } else if (selectedMarket === 'spreads') {
    // For spreads market, the data might come from h2h endpoint
    // Check for nested markets structure first
    const hasNestedMarkets = displayData && displayData.length > 0 && displayData[0].markets;

    if (hasNestedMarkets) {
      if (isFirstGame) {
      }
      const playerRecord = displayData[0];
      playerRecord.markets.forEach(marketData => {
        const { market, point, books } = marketData;
        if (market.startsWith('spreads_')) {
          Object.keys(books).forEach(bookName => {
            const teamData = books[bookName];
            Object.keys(teamData).forEach(teamName => {
              const odds = teamData[teamName];
              // Determine team based on the team name in the data
              const teamKey = teamName.toLowerCase().includes('arizona') ? 'away' : 
                             teamName.toLowerCase().includes('kansas state') ? 'home' : 
                             teamName.toLowerCase().includes(safeGame.away?.toLowerCase()) ? 'away' : 
                             teamName.toLowerCase().includes(safeGame.home?.toLowerCase()) ? 'home' :
                             teamName.toLowerCase().includes(safeGame.awayTeamFull?.toLowerCase()) ? 'away' : 
                             teamName.toLowerCase().includes(safeGame.homeTeamFull?.toLowerCase()) ? 'home' : 'unknown';
              
              
              let existingRecord = spreadsData.find(r => r.team === teamKey && r.point === point);
              if (!existingRecord) {
                existingRecord = {
                  playerId: `spreads_${point}_${teamKey}`,
                  team: teamKey,
                  point: point,
                  betType: 'spread'
                };
                spreadsData.push(existingRecord);
              }
              existingRecord[`spreads_${bookName}`] = odds;
            });
          });
        }
      });
    } else {
      spreadsData = filteredData.filter(p => p.playerId?.startsWith('spreads_'));
    }
    if (isFirstGame) {
    }
  } else if (selectedMarket === 'totals') {
    // For totals market, the data might come from h2h endpoint
    const hasNestedMarkets = displayData && displayData.length > 0 && displayData[0].markets;

    if (hasNestedMarkets) {
      const playerRecord = displayData[0];
      playerRecord.markets.forEach(marketData => {
        const { market, point, books } = marketData;
        if (market.startsWith('totals_')) {
          Object.keys(books).forEach(bookName => {
            const bookData = books[bookName];
            if (bookData.over !== undefined) {
              let overRecord = totalsData.find(r => r.betType === 'over' && r.point === point);
              if (!overRecord) {
                overRecord = { playerId: `totals_${point}_over`, point: point, betType: 'over' };
                totalsData.push(overRecord);
              }
              overRecord[`totals_${bookName}`] = bookData.over;
            }
            if (bookData.under !== undefined) {
              let underRecord = totalsData.find(r => r.betType === 'under' && r.point === point);
              if (!underRecord) {
                underRecord = { playerId: `totals_${point}_under`, point: point, betType: 'under' };
                totalsData.push(underRecord);
              }
              underRecord[`totals_${bookName}`] = bookData.under;
            }
          });
        }
      });
    } else {
      totalsData = filteredData.filter(p => p.playerId?.startsWith('totals_'));
    }
  }

  // Sort each dataset for consistent display
  h2hData.sort((a, b) => {
    if (a.team === 'away' && b.team === 'home') return -1;
    if (a.team === 'home' && b.team === 'away') return 1;
    return 0;
  });
  
  totalsData.sort((a, b) => {
    if (a.point !== b.point) return a.point - b.point;
    if (a.betType === 'over' && b.betType === 'under') return -1;
    if (a.betType === 'under' && b.betType === 'over') return 1;
    return 0;
  });
  
  spreadsData.sort((a, b) => {
    if (a.team !== b.team) {
      if (a.team === 'away' && b.team === 'home') return -1;
      if (a.team === 'home' && b.team === 'away') return 1;
    }
    return a.point - b.point;
  });

  // Show loading state
  if (!displayData && showLoading) {
    return (
      <div className="card game-card">
        <div className="card-body">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Always show the game card structure for NCAAF
  return (
    <div className={`card game-card ${isCollapsed ? 'collapsed' : ''}`} {...swipeHandlers}>
      <div className="card-body">
        {/* Use GameHeader component */}
        <GameHeader 
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={safeGame}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatLiveStatus}
          isLoading={showLoading}
          showScores={true}
        />

        {/* Mobile book navigation arrows */}
        {isMobile && totalPages > 1 && !isCollapsed && (
          <div className="mobile-book-nav">
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={goToPrevBooks}
              disabled={totalPages <= 1 || isTransitioning}
            >
              ←
            </button>
            <span className="mx-2">
              {mobileBookIndex + 1} / {totalPages}
            </span>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={goToNextBooks}
              disabled={totalPages <= 1 || isTransitioning}
            >
              →
            </button>
          </div>
        )}

        {/* Market selector */}
        {!isCollapsed && availableMarkets && availableMarkets.length > 0 && (
          <div className="market-selector mb-3">
            <div className="btn-group" role="group" aria-label="Market selection">
              {availableMarkets.map(market => (
                <React.Fragment key={market.key}>
                  <input
                    type="radio"
                    className="btn-check"
                    name={`market-${safeGame.gameIDmlb}`}
                    id={`market-${market.key}-${safeGame.gameIDmlb}`}
                    checked={selectedMarket === market.key}
                    onChange={() => onMarketSelection(safeGame.gameIDmlb, market.key)}
                    autoComplete="off"
                  />
                  <label
                    className={`btn ${selectedMarket === market.key ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                    htmlFor={`market-${market.key}-${safeGame.gameIDmlb}`}
                  >
                    {market.name}
                  </label>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Content based on data availability and collapse state */}
        {!isCollapsed && (
          <div className="odds-content">
            {!displayData || displayData.length === 0 ? (
              <div className="no-odds-message">
                <p>No odds available for this game</p>
              </div>
            ) : (
              <>
                {/* Desktop: All markets use _all tables with period filtering */}
                {!isMobile && (selectedMarket === 'h2h' || selectedMarket === 'h2h_all' || selectedMarket === 'h2h_quarters' || selectedMarket === 'h2h_halves') && (h2hData.length > 0 || totalsData.length > 0 || spreadsData.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <H2hAllTable
                      h2hData={h2hData}
                      bookLogos={bookLogos}
                      getBookLogoUrl={getBookLogoUrl}
                      getCellStyle={getCellStyle}
                      getTooltipText={getTooltipText}
                      onOddsClick={onOddsClick}
                      game={safeGame}
                      oddsCellStyle={oddsCellStyle}
                      periodsToShow={
                        selectedMarket === 'h2h' ? ['Game'] :
                        selectedMarket === 'h2h_quarters' ? ['Q1', 'Q2', 'Q3', 'Q4'] :
                        selectedMarket === 'h2h_halves' ? ['1st Half', '2nd Half'] :
                        null  // h2h_all shows all periods
                      }
                    />

                    <TotalsAllTable
                      totalsData={totalsData}
                      bookLogos={bookLogos}
                      getBookLogoUrl={getBookLogoUrl}
                      getCellStyle={getCellStyle}
                      getTooltipText={getTooltipText}
                      onOddsClick={onOddsClick}
                      game={safeGame}
                      oddsCellStyle={oddsCellStyle}
                      periodsToShow={
                        selectedMarket === 'h2h' ? ['Game'] :
                        selectedMarket === 'h2h_quarters' ? ['Q1', 'Q2', 'Q3', 'Q4'] :
                        selectedMarket === 'h2h_halves' ? ['1st Half', '2nd Half'] :
                        null  // h2h_all shows all periods
                      }
                    />

                    <SpreadsAllTable
                      spreadsData={spreadsData}
                      bookLogos={bookLogos}
                      getBookLogoUrl={getBookLogoUrl}
                      getTeamLogoUrl={getTeamLogoUrl}
                      getCellStyle={getCellStyle}
                      getTooltipText={getTooltipText}
                      onOddsClick={onOddsClick}
                      game={safeGame}
                      oddsCellStyle={oddsCellStyle}
                      periodsToShow={
                        selectedMarket === 'h2h' ? ['Game'] :
                        selectedMarket === 'h2h_quarters' ? ['Q1', 'Q2', 'Q3', 'Q4'] :
                        selectedMarket === 'h2h_halves' ? ['1st Half', '2nd Half'] :
                        null  // h2h_all shows all periods
                      }
                    />
                  </div>
                )}

                {/* Desktop: Individual spreads table */}
                {!isMobile && selectedMarket === 'spreads' && spreadsData.length > 0 && (
                  <div>
                    <div className="table-container">
                      <Table bordered size="sm" className="text-center">
                        <thead>
                          <tr><th colSpan={bookLogos.length + 1} className="bg-light">Spreads</th></tr>
                          <tr>
                            <th style={{ width: '120px' }}></th>
                            {bookLogos.map(book => (
                              <th key={`spreads-${book}`} style={oddsCellStyle}>
                                <img 
                                  src={getBookLogoUrl(book)} 
                                  alt={`${book} logo`} 
                                  title={book} 
                                  style={{ height: '20px' }} 
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {spreadsData.map((player, index) => (
                            <tr key={player.playerId || `spread-${index}`}>
                              <td style={{ width: '120px', fontWeight: 'bold' }}>
                                {(player.team === 'away' || player.team === 'home') ? (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                    <img
                                      src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                                      alt={player.team === 'away' ? safeGame.away : safeGame.home}
                                      style={{ width: '20px', height: '20px' }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                      <span>{player.team === 'away' ? (safeGame.awayTeamFull || safeGame.away) : (safeGame.homeTeamFull || safeGame.home)}</span>
                                      {player.point !== null && player.point !== undefined && (
                                        <span style={{ 
                                          color: '#9CA3AF', 
                                          fontSize: '0.85rem',
                                          fontWeight: 'normal'
                                        }}>
                                          {player.point > 0 ? `+${player.point}` : player.point}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  formatPlayerName(player.player || player.name)
                                )}
                              </td>
                              {bookLogos.map(book => {
                                const odds = player[`spreads_${book}`] || player[book];
                                return (
                                  <td
                                    key={book}
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, 'spreads', book, player.point, 'spread')}
                                    style={{
                                      ...oddsCellStyle,
                                      cursor: odds ? 'pointer' : 'default',
                                      opacity: odds ? 1 : 0.5
                                    }}
                                    onClick={() => {
                                      if (onOddsClick && odds) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name || player.player, 'spreads', book, odds, safeGame.away, safeGame.home);
                                      }
                                    }}
                                  >
                                    {odds ? (
                                      <span style={{ color: odds > 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                                        {odds > 0 ? `+${odds}` : odds}
                                      </span>
                                    ) : (
                                      '\u00A0'
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Desktop: Individual totals table */}
                {!isMobile && selectedMarket === 'totals' && totalsData.length > 0 && (
                  <div>
                    <div className="table-container">
                      <Table bordered size="sm" className="text-center">
                        <thead>
                          <tr><th colSpan={bookLogos.length + 1} className="bg-light">Totals</th></tr>
                          <tr>
                            <th style={{ width: '120px' }}></th>
                            {bookLogos.map(book => (
                              <th key={`totals-${book}`} style={oddsCellStyle}>
                                <img 
                                  src={getBookLogoUrl(book)} 
                                  alt={`${book} logo`} 
                                  title={book} 
                                  style={{ height: '20px' }} 
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {totalsData.map((player, index) => (
                            <tr key={player.playerId || `total-${index}`}>
                              <td style={{ width: '120px', fontWeight: 'bold' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                  <span>{player.betType === 'over' ? 'Over' : 'Under'}</span>
                                  {player.point !== null && player.point !== undefined && (
                                    <span style={{ 
                                      color: '#9CA3AF', 
                                      fontSize: '0.85rem',
                                      fontWeight: 'normal'
                                    }}>
                                      {player.point}
                                    </span>
                                  )}
                                </div>
                              </td>
                              {bookLogos.map(book => {
                                const odds = player[`totals_${book}`] || player[book];
                                return (
                                  <td
                                    key={book}
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, 'totals', book, player.point, player.betType || 'over')}
                                    style={{
                                      ...oddsCellStyle,
                                      cursor: odds ? 'pointer' : 'default',
                                      opacity: odds ? 1 : 0.5
                                    }}
                                    onClick={() => {
                                      if (onOddsClick && odds) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name || player.player, 'totals', book, odds, safeGame.away, safeGame.home);
                                      }
                                    }}
                                  >
                                    {odds ? (
                                      <span style={{ color: odds > 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                                        {odds > 0 ? `+${odds}` : odds}
                                      </span>
                                    ) : (
                                      '\u00A0'
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Regular player props table for non-team markets */}
                {/* Mobile view for h2h markets - use mini tables */}
                {isMobile && (selectedMarket === 'h2h' || selectedMarket === 'h2h_all' || selectedMarket === 'h2h_quarters' || selectedMarket === 'h2h_halves') && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <H2hMiniTable
                        h2hData={h2hData}
                        getBookLogoUrl={getBookLogoUrl}
                        getTeamLogoUrl={getTeamLogoUrl}
                        getCellStyle={getCellStyle}
                        getTooltipText={getTooltipText}
                        onOddsClick={onOddsClick}
                        game={safeGame}
                        periodsToShow={
                          selectedMarket === 'h2h' ? ['Game'] :
                          selectedMarket === 'h2h_quarters' ? ['Q1', 'Q2', 'Q3', 'Q4'] :
                          selectedMarket === 'h2h_halves' ? ['1st Half', '2nd Half'] :
                          null  // h2h_all shows all periods
                        }
                      />
                      <TotalsMiniTable
                        totalsData={totalsData}
                        getBookLogoUrl={getBookLogoUrl}
                        getCellStyle={getCellStyle}
                        getTooltipText={getTooltipText}
                        onOddsClick={onOddsClick}
                        game={safeGame}
                        periodsToShow={
                          selectedMarket === 'h2h' ? ['Game'] :
                          selectedMarket === 'h2h_quarters' ? ['Q1', 'Q2', 'Q3', 'Q4'] :
                          selectedMarket === 'h2h_halves' ? ['1st Half', '2nd Half'] :
                          null
                        }
                      />
                    </div>
                    <SpreadsMiniTable
                      spreadsData={spreadsData}
                      getBookLogoUrl={getBookLogoUrl}
                      getTeamLogoUrl={getTeamLogoUrl}
                      getCellStyle={getCellStyle}
                      getTooltipText={getTooltipText}
                      onOddsClick={onOddsClick}
                      game={safeGame}
                      periodsToShow={
                        selectedMarket === 'h2h' ? ['Game'] :
                        selectedMarket === 'h2h_quarters' ? ['Q1', 'Q2', 'Q3', 'Q4'] :
                        selectedMarket === 'h2h_halves' ? ['1st Half', '2nd Half'] :
                        null
                      }
                    />
                  </div>
                )}

                {/* Mobile view for other markets */}
                {isMobile && !['h2h', 'h2h_all', 'spreads', 'totals', 'h2h_quarters', 'h2h_halves'].includes(selectedMarket) && (
                  <div className="table-responsive">
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr>
                          <th style={{ width: '150px' }}>Player</th>
                          {bookLogos.map(book => (
                            <th key={book} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={book}
                                style={{ height: '20px', width: 'auto' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map(player => (
                          <tr key={player.id || `${player.player}-${player.playerId}`}>
                            <td className="fw-bold">{formatPlayerName(player.player || player.name)}</td>
                            {bookLogos.map(book => {
                              const bookOdds = player[book];
                              
                              return (
                                <td key={book} style={oddsCellStyle}>
                                  {bookOdds ? (
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.player || player.name, selectedMarket, book, bookOdds, safeGame.away, safeGame.home)}
                                      style={{ fontSize: '12px', padding: '2px 6px' }}
                                    >
                                      {bookOdds > 0 ? `+${bookOdds}` : bookOdds}
                                    </button>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Last update timestamp */}
        {lastUpdate && !isCollapsed && (
          <div className="text-center mt-2">
            <small className="text-muted">
              Last updated: {new Date(lastUpdate).toLocaleTimeString()}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default NCAAFDataTable;